import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";
import { CardWrapper } from "../../ui";
import HistoryFilters from "./HistoryFilters";
import HistoryRow, { FieldLabel } from "./HistoryRow";
import HistoryCard from "./HistoryCard";
import ReviewDialog from "./ReviewDialog";
import { useAuth } from "../../../context/AuthContext";
import {
  downloadRegMPreClearPdf,
  fetchRegMPreClearRecords,
  fetchRegMReviews,
  verifyRegMPreClear,
} from "../../../services/Axios";
import { errorMessage, savePdfBlob } from "./regmFormat";
import {
  assertRecordList,
  clampPage,
  defaultFilters,
  filterRecords,
  HISTORY_FIELDS,
  paginate,
  reviewAction,
  showTableView,
  shouldApplyRoleDefault,
  shouldAutoVerify,
  toggleFilter,
} from "./historyFormat";

const ROWS_PER_PAGE = 25;

const HistoryTab = () => {
  const { claims } = useAuth();
  const roles = claims?.roles;
  const [dialog, setDialog] = useState(null); // { record, action }
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  // Compliance starts on "Pending review"; applied once, as soon as the roles are known,
  // and never over a filter the user has already changed.
  const [filters, setFilters] = useState(() => defaultFilters(roles));
  const defaultsApplied = useRef(Array.isArray(roles));
  const filtersTouched = useRef(false);
  const [page, setPage] = useState(0);
  // Per-row state keyed by record id: { loading, result, error }.
  const [verifyState, setVerifyState] = useState({});
  const [pdfState, setPdfState] = useState({});
  // Expanded rows and their review history ({ status: "loading" | "failed" } or { status: "loaded", items }).
  const [expanded, setExpanded] = useState({});
  const [reviewsState, setReviewsState] = useState({});
  // Rows whose automatic first Verify has already run.
  const autoVerified = useRef(new Set());
  const loadId = useRef(0);
  // Per-row request tokens: a late verify, pdf or reviews response for an older request is ignored.
  const rowToken = useRef({});
  const tokenCounter = useRef(0);
  const nextToken = (kind, id) => {
    const key = `${kind}:${id}`;
    tokenCounter.current += 1;
    rowToken.current[key] = tokenCounter.current;
    return tokenCounter.current;
  };
  const isCurrent = (kind, id, token) => rowToken.current[`${kind}:${id}`] === token;
  // Table or cards by the measured width of this container (the drawer eats viewport width, so the
  // viewport is not a good guide). Only one is rendered, so row state is never duplicated.
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(null);
  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const measure = () => setContainerWidth(node.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const wide = showTableView(containerWidth);

  useEffect(() => {
    if (
      !shouldApplyRoleDefault({
        applied: defaultsApplied.current,
        touched: filtersTouched.current,
        roles,
      })
    ) {
      return;
    }
    defaultsApplied.current = true;
    setFilters(defaultFilters(roles));
  }, [roles]);

  const load = useCallback(async (keepRowState = false) => {
    const myId = (loadId.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchRegMPreClearRecords();
      if (myId !== loadId.current) return;
      setRecords(assertRecordList(data));
      if (!keepRowState) {
        rowToken.current = {};
        autoVerified.current = new Set();
        setVerifyState({});
        setPdfState({});
        setExpanded({});
        setReviewsState({});
      }
    } catch (error) {
      if (myId !== loadId.current) return;
      setLoadError(error?.response ? errorMessage(error) : error?.message || errorMessage(error));
    } finally {
      if (myId === loadId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (setter, id, value) =>
    setter((prev) => ({ ...prev, [id]: value }));

  const handleVerify = async (id) => {
    const token = nextToken("verify", id);
    patch(setVerifyState, id, { loading: true });
    try {
      const result = await verifyRegMPreClear(id);
      if (!isCurrent("verify", id, token)) return;
      if (!result || typeof result !== "object") {
        throw new Error("Unexpected response from server");
      }
      patch(setVerifyState, id, { result });
    } catch (error) {
      if (!isCurrent("verify", id, token)) return;
      patch(setVerifyState, id, {
        error: error?.response ? errorMessage(error) : error?.message || errorMessage(error),
      });
    }
  };

  // Quiet on failure (no banner): the row shows a short note and Retry.
  const handleReviews = async (id) => {
    const token = nextToken("reviews", id);
    patch(setReviewsState, id, { status: "loading" });
    try {
      const data = await fetchRegMReviews(id);
      if (!isCurrent("reviews", id, token)) return;
      patch(setReviewsState, id, Array.isArray(data) ? { status: "loaded", items: data } : { status: "failed" });
    } catch {
      if (isCurrent("reviews", id, token)) patch(setReviewsState, id, { status: "failed" });
    }
  };

  const handlePdf = async (id) => {
    const token = nextToken("pdf", id);
    patch(setPdfState, id, { loading: true });
    try {
      const blob = await downloadRegMPreClearPdf(id);
      if (!isCurrent("pdf", id, token)) return;
      savePdfBlob(blob, id);
      patch(setPdfState, id, {});
    } catch (error) {
      if (!isCurrent("pdf", id, token)) return;
      patch(setPdfState, id, { error: errorMessage(error) });
    }
  };

  const handleToggle = (id) => {
    const opening = expanded[id] !== true;
    patch(setExpanded, id, opening);
    if (!opening) return;
    if (shouldAutoVerify({ opening, autoVerified: autoVerified.current.has(id), verify: verifyState[id] })) {
      autoVerified.current.add(id);
      handleVerify(id);
    }
    if (reviewsState[id]?.status !== "loaded" && reviewsState[id]?.status !== "loading") handleReviews(id);
  };

  const handleReviewed = (id) => {
    setDialog(null);
    // The review changes what verify reports for this row; other rows keep their state.
    nextToken("verify", id);
    nextToken("pdf", id);
    patch(setVerifyState, id, {});
    patch(setPdfState, id, {});
    patch(setReviewsState, id, undefined);
    autoVerified.current.delete(id);
    // The review is stored when onDone fires, so an open row re-verifies at once (tokens still guard it).
    if (expanded[id] === true) {
      autoVerified.current.add(id);
      handleVerify(id);
      handleReviews(id);
    }
    load(true);
  };

  const changeFilters = (next) => {
    filtersTouched.current = true;
    setFilters(next);
    setPage(0);
  };

  const filtered = filterRecords(records, { query, ...filters });
  const safePage = clampPage(page, filtered.length, ROWS_PER_PAGE);
  const rows = paginate(filtered, safePage, ROWS_PER_PAGE);

  // Same props, handlers and per-row state for a table row and a card.
  const rowProps = (r) => ({
    record: r,
    open: expanded[r.id] === true,
    onToggle: handleToggle,
    verify: verifyState[r.id] || {},
    onVerify: () => handleVerify(r.id),
    reviews: reviewsState[r.id],
    onRetryReviews: () => handleReviews(r.id),
    pdf: pdfState[r.id] || {},
    onPdf: handlePdf,
    action: reviewAction(roles, r),
    onReview: (record, action) => setDialog({ record, action }),
  });

  return (
    <CardWrapper>
      <Stack spacing={2} ref={containerRef}>
        <HistoryFilters
          query={query}
          onQueryChange={(value) => {
            filtersTouched.current = true;
            setQuery(value);
            setPage(0);
          }}
          filters={filters}
          onToggle={(key) => changeFilters(toggleFilter(filters, key))}
          onRefresh={() => load()}
          loading={loading}
          counts={loadError ? null : { shown: filtered.length, total: records.length }}
        />

        {loadError && <Alert severity="error">{loadError}</Alert>}
        {!loadError && !loading && records.length === 0 && (
          <Alert severity="info">No records yet.</Alert>
        )}

        {!loadError && records.length > 0 && filtered.length === 0 && (
          <Alert severity="info">No records match current filters.</Alert>
        )}

        {!loadError && records.length > 0 && (
          <>
            {wide ? (
              // No inner scroll box: the page (main, the app's scroll container) scrolls and the header
              // sticks to its top. overflow visible overrides the theme's hidden, which would trap sticky.
              <TableContainer sx={{ overflow: "visible" }}>
                <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { px: 1.5 } }}>
                  <TableHead>
                    <TableRow>
                      {HISTORY_FIELDS.map((f) => (
                        <TableCell key={f.key} sx={{ top: 0, fontWeight: 600, bgcolor: "background.paper" }}>
                          <FieldLabel field={f.key} label={f.label} />
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <HistoryRow key={r.id} {...rowProps(r)} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box component="ul" aria-label="Records" sx={{ listStyle: "none", m: 0, p: 0, minWidth: 0, borderTop: 1, borderColor: "divider" }}>
                {rows.map((r) => (
                  <HistoryCard key={r.id} {...rowProps(r)} />
                ))}
              </Box>
            )}
            <TablePagination
              component="div"
              count={filtered.length}
              page={safePage}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={ROWS_PER_PAGE}
              rowsPerPageOptions={[ROWS_PER_PAGE]}
            />
          </>
        )}
      </Stack>
      <ReviewDialog
        open={Boolean(dialog)}
        record={dialog?.record}
        action={dialog?.action}
        onClose={() => setDialog(null)}
        onDone={handleReviewed}
      />
    </CardWrapper>
  );
};

export default HistoryTab;
